
// ID First TODO in AUTH-7606: This component needs to handle the error scenario
// for a user who has MFA required but not set up yet
// Libraries
import { PropType, defineComponent } from 'vue';

// Components
import DuoUserLoginHandler from 'client/containers/user/MfaComponents/LoginHandlers/DuoUserLoginHandler.vue';
import DuoUserResetPasswordHandler from 'client/containers/user/UserResetPassword/UserResetPasswordMfaHandlers/DuoUserResetPasswordHandler.vue';
import EmailBadge from 'client/containers/user/EmailBadge.vue';
import LoginCard from 'client/components/LoginCard.vue';
import MfaChooser from 'client/containers/user/MfaComponents/MfaChooser.vue';
import PushUserLoginHandler from 'client/containers/user/MfaComponents/LoginHandlers/PushUserLoginHandler.vue';
import PushUserResetPasswordHandler from 'client/containers/user/UserResetPassword/UserResetPasswordMfaHandlers/PushUserResetPasswordHandler.vue';
import TotpUserLoginHandler from 'client/containers/user/MfaComponents/LoginHandlers/TotpUserLoginHandler.vue';
import TotpUserResetPasswordHandler from 'client/containers/user/UserResetPassword/UserResetPasswordMfaHandlers/TotpUserResetPasswordHandler.vue';
import WebAuthnUserLoginHander from 'client/containers/user/MfaComponents/LoginHandlers/WebAuthnUserLoginHandler.vue';
import WebAuthnUserResetPasswordHandler from 'client/containers/user/UserResetPassword/UserResetPasswordMfaHandlers/WebAuthnUserResetPasswordHandler.vue';

// Utils
import { webAuthnSupported } from 'client/util/FeatureSupport';
import LocalStorageService from 'client/util/LocalStorageService';
import getCurrentComponentNameFromList from 'client/containers/user/MfaComponents/mfaUtils/getCurrentComponentNameFromList';

// Constants
import { LocalStorageConsts } from 'client/util/Constants';
import { useMfaLabels } from 'client/util/Constants/MfaLabels';
import MfaConstants from 'client/containers/Login/MfaConstants';

// Store
import { mapStores } from 'pinia';
import UserLoginStore from 'client/stores/UserLoginStore';

// Types
import {
  Duo, Push, Totp, WebAuthn,
} from 'client/containers/user/MfaComponents/mfaUtils/mfaTypes';

const { previousMfaMethod } = LocalStorageConsts;

export default defineComponent({
  name: 'MfaController',

  components: {
    DuoUserLoginHandler,
    DuoUserResetPasswordHandler,
    EmailBadge,
    LoginCard,
    MfaChooser,
    PushUserLoginHandler,
    PushUserResetPasswordHandler,
    TotpUserLoginHandler,
    TotpUserResetPasswordHandler,
    WebAuthnUserLoginHander,
    WebAuthnUserResetPasswordHandler,
  },

  props: {
    controllingQueryParams: {
      type: Object,
      default: () => ({}),
    },
    componentsUnderControl: {
      type: Array as PropType<{id: string, componentName: string }[]>,
      required: true,
    },
    errorMessage: {
      type: String,
      default: '',
    },
    factors: {
      type: Array as PropType<(Duo | Totp | Push | WebAuthn)[]>,
      required: true,
    },
    form: {
      type: Object,
      default: () => {},
    },
    mfaType: {
      type: String,
      required: true,
    },
    requestKey: {
      type: String,
      default: '',
    },
    shouldHideEmailBadge: {
      type: Boolean,
      default: false,
    },
  },

  setup() {
    const uiLabel = useMfaLabels();
    return { uiLabel };
  },

  beforeMount() {
    let lastFactor = this.factors.find(
      (factor) => factor.name === LocalStorageService.getItem(previousMfaMethod),
    );

    // WebAuthn edge cases
    // If webauthn is last factor and is no longer supported there are three paths:
    // 1) if it's the only factor redirect anyway
    // 2) if one other factor is available then redirect to it
    // 3) if multiple other factors are available then stay on /choosemfa
    if (
      lastFactor
      && lastFactor.name === this.webauthn
      && !webAuthnSupported()
    ) {
      const remainingFactors = this.factors.filter(
        (factor) => factor.name !== this.webauthn,
      );

      lastFactor = remainingFactors.length === 1
        ? remainingFactors[0]
        : undefined;
    }

    if (lastFactor === undefined) {
      LocalStorageService.removeItem(previousMfaMethod);
    }

    const factor = this.factors.length === 1
      ? this.factors[0]
      : lastFactor;

    const onCurrentFactor = factor && this.mfaType !== factor.name;

    if (onCurrentFactor) {
      this.handleChangeFactor(factor.name);
    }
  },

  computed: {
    ...mapStores(UserLoginStore),

    mfaComponent() {
      const getCurrentComponentName = getCurrentComponentNameFromList(this.componentsUnderControl);

      return getCurrentComponentName(this.mfaType);
    },

    availableFactors() {
      return this.factors.filter((factor) => factor.name !== this.mfaType);
    },

    hasAlternateFactorsAvailable() {
      return this.factors.length > 1;
    },

    hasSelectedFactor() {
      return !!this.mfaType;
    },

    shouldShowDivider() {
      return this.hasAlternateFactorsAvailable && this.hasSelectedFactor;
    },

    message() {
      return 'This extra step shows you\'re really you. Choose a method to verify yourself:';
    },

    webauthn() {
      return MfaConstants.mfaNames.webauthn;
    },
  },

  methods: {
    handleChangeFactor(factorName: string) {
      const factor = this.factors.find((f) => factorName === f.name);

      if (!factor) {
        return;
      }

      this.$router.replace(factor.route);

      // Clear any error that may have occurred in a different factor
      this.resetErrorMessage();
    },

    // ID First TODO: Define error
    handleError(error: any) {
      this.$emit('onError', error);
    },

    handleSuccess() {
      this.$emit('onSuccess');
    },

    resetErrorMessage() {
      if (this.errorMessage) {
        this.$emit('onResetErrorMessage');
      }
    },
  },
});
